/**
 * KYCContext — extracted from UserContext
 *
 * Holds KYC / compliance state:
 * - kycStatus, kycProvider, emailVerified
 * - refreshKYCStatus() — calls the Plaid IDV status API
 * - setKYCStatus()    — for webhook/polling updates
 *
 * Mount order requirement:
 *   <AuthProvider>        ← outermost auth
 *     <KYCProvider>       ← uses useAuth() to get userId for API calls
 *       ...rest of app
 *     </KYCProvider>
 *   </AuthProvider>
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { kycService } from '../services/kyc';
import config from '../config';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type KYCStatus =
  | 'not_started'
  | 'none'
  | 'active'
  | 'pending'
  | 'pending_review'
  | 'submitted'
  | 'approved'
  | 'verified'
  | 'failed'
  | 'rejected'
  | 'expired'
  | null;

export interface KYCContextType {
  /** Current KYC status as reported by the backend. */
  kycStatus: KYCStatus;

  /** KYC provider name (e.g. 'plaid'). Present once a session has been created. */
  kycProvider: string | undefined;

  /** Whether the currently logged-in user has verified their email address. */
  emailVerified: boolean;

  /** Whether the user's identity is fully verified ('verified' status). */
  isVerified: boolean;

  /** Re-fetches the KYC status from the backend. */
  refreshKYCStatus(): Promise<void>;

  /** Directly set KYC status (for use after webhook/polling confirmation). */
  setKYCStatus(status: KYCStatus): void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------
const KYCContext = createContext<KYCContextType | undefined>(undefined);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------
interface KYCProviderProps {
  children: ReactNode;
}

export function KYCProvider({ children }: KYCProviderProps) {
  const { user, isAuthenticated } = useAuth();

  const [kycStatus, setKYCStatusState] = useState<KYCStatus>(null);
  const [kycProvider, setKycProvider] = useState<string | undefined>(undefined);

  // Derive emailVerified directly from the auth user object so it stays in sync.
  const emailVerified = user?.email_verified ?? false;
  const isVerified = kycStatus === 'verified' || kycStatus === 'approved';

  // -------------------------------------------------------------------------
  // Hydrate KYC status when the user becomes authenticated
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (config.features.kyc && isAuthenticated && user) {
      // Seed from the user object first (already available, zero latency)
      const statusFromUser = user.kyc_status as KYCStatus;
      if (statusFromUser) {
        setKYCStatusState(statusFromUser);
      }

      // Then refetch from the dedicated KYC endpoint for freshness
      refreshKYCStatus().catch(err =>
        console.warn('[KYCContext] Initial KYC status check failed:', err)
      );
    } else if (!isAuthenticated) {
      // Reset on logout
      setKYCStatusState(null);
      setKycProvider(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id]);

  // -------------------------------------------------------------------------
  // refreshKYCStatus
  // -------------------------------------------------------------------------
  const refreshKYCStatus = useCallback(async (): Promise<void> => {
    if (!config.features.kyc || !isAuthenticated) return;

    try {
      console.log('[KYCContext] Fetching KYC status from backend...');
      const response = await kycService.getKycStatus();

      if (response.success && response.data) {
        const newStatus = response.data.status as KYCStatus;
        console.log('[KYCContext] KYC status updated:', newStatus);
        setKYCStatusState(newStatus);

        // kycService uses Plaid — record provider name
        setKycProvider('plaid');
      }
    } catch (err) {
      console.warn('[KYCContext] Failed to refresh KYC status:', err);
    }
  }, [isAuthenticated]);

  // -------------------------------------------------------------------------
  // setKYCStatus — public imperative setter (webhook / polling path)
  // -------------------------------------------------------------------------
  const setKYCStatus = useCallback((status: KYCStatus): void => {
    console.log('[KYCContext] setKYCStatus called with:', status);
    setKYCStatusState(status);
  }, []);

  // -------------------------------------------------------------------------
  // Context value
  // -------------------------------------------------------------------------
  const value: KYCContextType = {
    kycStatus,
    kycProvider,
    emailVerified,
    isVerified,
    refreshKYCStatus,
    setKYCStatus,
  };

  return <KYCContext.Provider value={value}>{children}</KYCContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export function useKYC(): KYCContextType {
  const context = useContext(KYCContext);
  if (context === undefined) {
    throw new Error(
      '[KYCContext] useKYC() must be called inside <KYCProvider>. ' +
        'Ensure <KYCProvider> is mounted inside <AuthProvider>.'
    );
  }
  return context;
}

export { KYCContext };
export default KYCContext;
